import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { Group, Page } from '../../../types';
import { useLocation, useNavigate } from 'react-router-dom';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import { Messages } from '../../utils/message-constants';
import { useUserSettings } from '../../utils/use-user-settings';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  getDomainsQuery,
  getGroupsUserPortal,
  UserKeys,
} from '../../api/users-api';
import { pagesConfig } from '../../shared/pages-config';
import AllGroupsGridToolbar from './all-groups-toolbar';
import AddGroupDialog from './add-group-dialog';
import SharedDataGridServer from '../../shared/shared-data-grid-server';
import { useToolbar } from '../../shared/shared-data-grid-toolbar-helper';
import { userPortalPagesConfig } from '../../shared/user-portal-pages-config';
import GroupRemove from '@mui/icons-material/GroupRemove';

export default function AllGroupsGrid() {
  const { limitedVisDomainList, limitedVisEnabled } = useUserSettings();
  const [openAddGroup, setOpenAddGroup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );
  const { data: allDomains } = useQuery(getDomainsQuery(userView));

  const domains = limitedVisEnabled ? limitedVisDomainList : allDomains || [];

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
  }>({
    pageSize: 50,
    page: 0,
    cursor: null,
    search: '',
  });

  const {
    data: groups,
    isLoading: groupsLoading,
    isFetching: groupsFetching,
  } = useQuery({
    queryKey: [
      UserKeys.Groups,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.search,
    ],
    queryFn: () => {
      const object: Page<Group[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      if (paginationModel.page !== 0 && groups && groups.queries) {
        object.queries = {
          [Object.keys(groups.queries)[0]]: paginationModel.cursor || '',
        };
      }
      return getGroupsUserPortal(object, paginationModel.search);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('groups'),
    },
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'name',
        headerName: 'Name',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'directMemberCount',
        headerName: 'Member Count',
        editable: false,
        sortable: false,
        filterable: false,
        width: 130,
        align: 'center',
      },
    ],
    []
  );

  const toolbarProps = useMemo(
    () => ({
      paginationModel,
      setPaginationModel,
      setOpenAddGroup,
    }),
    [paginationModel]
  );

  const CustomToolbar = useToolbar(AllGroupsGridToolbar, toolbarProps);

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays(
            'Groups',
            <GroupRemove />,
            paginationModel.search !== ''
              ? 'Try changing your search term.'
              : ''
          ),
        }}
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        getRowId={(row) => row.email}
        data={groups}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (groupsLoading || groupsFetching) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              search: paginationModel.search,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: groups?.cursor || '',
              search: paginationModel.search,
            });
          }
        }}
        onFilterModelChange={(model) => {
          if (groupsLoading || groupsFetching) return;

          setPaginationModel({
            ...paginationModel,
            search: model.quickFilterValues?.join(' ') ?? '',
          });
        }}
        loading={groupsLoading || groupsFetching}
        onRowClick={(row: GridRowParams<Group>) =>
          navigate(`${pagesConfig.groups.link}/${row.row.email}`)
        }
      />
      {openAddGroup && (
        <AddGroupDialog
          open={openAddGroup}
          handleClose={() => setOpenAddGroup(false)}
          domains={domains}
        />
      )}
    </>
  );
}
